import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { patientAPI } from '../services/api';
import { format } from 'date-fns';

const PatientList = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  const fetchPatients = async (page = 1) => {
    try {
      setLoading(true);
      const response = await patientAPI.getAll(page, pagination.limit);
      setPatients(response.data.patients);
      setPagination(response.data.pagination);
      setError('');
    } catch (err) {
      setError('Failed to load patients');
      console.error('Error fetching patients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this patient?')) {
      return;
    }

    try {
      await patientAPI.delete(id);
      fetchPatients(pagination.page);
    } catch (err) {
      setError('Failed to delete patient');
      console.error('Error deleting patient:', err);
    }
  };

  const handlePageChange = (newPage) => {
    fetchPatients(newPage);
  };

  if (loading && patients.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500 text-lg">Loading patients...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Patients</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage your patient records
          </p>
        </div>
        <Link
          to="/dashboard/patients/new"
          className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          + Add New Patient
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Patients Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {patients.map(patient => (
          <div
            key={patient.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
          >
            {/* Patient Card Header */}
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {patient.name}
              </h3>
              
              {/* Patient Details */}
              <div className="space-y-2 text-sm">
                <p className="flex items-start">
                  <span className="text-gray-500 w-20">Email:</span>
                  <span className="text-gray-700 flex-1">{patient.email || 'N/A'}</span>
                </p>
                <p className="flex items-start">
                  <span className="text-gray-500 w-20">Phone:</span>
                  <span className="text-gray-700">{patient.phone || 'N/A'}</span>
                </p>
                <p className="flex items-start">
                  <span className="text-gray-500 w-20">DOB:</span>
                  <span className="text-gray-700">
                    {patient.dob ? format(new Date(patient.dob), 'MM/dd/yyyy') : 'N/A'}
                  </span>
                </p>
                <p className="flex items-start">
                  <span className="text-gray-500 w-20">Notes:</span>
                  <span className="text-gray-700 flex-1">
                    {patient.medical_notes 
                      ? patient.medical_notes.substring(0, 50) + (patient.medical_notes.length > 50 ? '...' : '')
                      : 'None'}
                  </span>
                </p>
                <p className="flex items-start">
                  <span className="text-gray-500 w-20">Created:</span>
                  <span className="text-gray-700">
                   {format(new Date(patient.created_at), 'MMM dd, yyyy  hh:mm a')}
                  </span>
                </p>
              </div>
            </div>

            {/* Card Actions */}
            <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg flex gap-2">
              <Link
                to={`/dashboard/chat/${patient.id}`}
                className="flex-1 px-3 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-center transition-colors"
              >
                Chat
              </Link>
              <Link
                to={`/dashboard/patients/edit/${patient.id}`}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-center transition-colors"
              >
                Edit
              </Link>
              <button
                onClick={() => handleDelete(patient.id)}
                className="flex-1 px-3 py-2 bg-white text-red-600 text-sm font-medium rounded-md border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {patients.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No patients yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first patient</p>
          <Link
            to="/dashboard/patients/new"
            className="inline-flex px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Add New Patient
          </Link>
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center mt-8">
          <nav className="flex items-center gap-1" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              Previous
            </button>
            
            {[...Array(pagination.pages)].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    pagination.page === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
              className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default PatientList;